import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { $isLinkNode, LinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListItemNode,
  ListNode,
  REMOVE_LIST_COMMAND,
} from "@lexical/list";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { $patchStyleText } from "@lexical/selection";
import { mergeRegister } from "@lexical/utils";
import axios from "axios";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_LOW,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  INDENT_CONTENT_COMMAND,
  OUTDENT_CONTENT_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  TextNode,
  UNDO_COMMAND,
} from "lexical";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, ModalFooter } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import GmailComposeStyles from "./GmailComposeStyles";
import useInbox from "./InboxContext";

const FONT_FAMILIES = [
  { label: "Sans Serif", value: "Arial, Helvetica, sans-serif" },
  { label: "Serif", value: '"Times New Roman", Times, serif' },
  { label: "Fixed Width", value: '"Courier New", Courier, monospace' },
  { label: "Comic Sans", value: '"Comic Sans MS", cursive, sans-serif' },
];

const FONT_SIZE_OPTIONS = [
  { label: "Small", value: "12px" },
  { label: "Normal", value: "14px" },
  { label: "Large", value: "18px" },
  { label: "Huge", value: "24px" },
];

const DEFAULT_TEXT_COLOR = "#1f2937";
const DEFAULT_BG_COLOR = "transparent";

const patchStyleConversion = (originalDOMConverter) => (node) => {
  const original = originalDOMConverter?.(node);
  if (!original) {
    return null;
  }

  const originalOutput = original.conversion(node);
  if (!originalOutput) {
    return originalOutput;
  }

  const backgroundColor = node.style.backgroundColor;
  const color = node.style.color;
  const fontFamily = node.style.fontFamily;
  const fontWeight = node.style.fontWeight;
  const fontSize = node.style.fontSize;
  const textDecoration = node.style.textDecoration;

  return {
    ...originalOutput,
    forChild: (lexicalNode, parent) => {
      const output = originalOutput.forChild
        ? originalOutput.forChild(lexicalNode, parent)
        : lexicalNode;

      if ($isTextNode(output)) {
        const style = [
          backgroundColor ? `background-color: ${backgroundColor}` : null,
          color ? `color: ${color}` : null,
          fontFamily ? `font-family: ${fontFamily}` : null,
          fontWeight ? `font-weight: ${fontWeight}` : null,
          fontSize ? `font-size: ${fontSize}` : null,
          textDecoration ? `text-decoration: ${textDecoration}` : null,
        ]
          .filter(Boolean)
          .join("; ");

        if (style) {
          return output.setStyle(style);
        }
      }

      return output;
    },
  };
};

class ExtendedTextNode extends TextNode {
  static getType() {
    return "extended-text";
  }

  static clone(node) {
    return new ExtendedTextNode(node.__text, node.__key);
  }

  static importDOM() {
    const importers = TextNode.importDOM();

    return {
      ...importers,
      code: () => ({
        conversion: patchStyleConversion(importers?.code),
        priority: 1,
      }),
      em: () => ({
        conversion: patchStyleConversion(importers?.em),
        priority: 1,
      }),
      span: () => ({
        conversion: patchStyleConversion(importers?.span),
        priority: 1,
      }),
      strong: () => ({
        conversion: patchStyleConversion(importers?.strong),
        priority: 1,
      }),
      sub: () => ({
        conversion: patchStyleConversion(importers?.sub),
        priority: 1,
      }),
      sup: () => ({
        conversion: patchStyleConversion(importers?.sup),
        priority: 1,
      }),
      u: () => ({
        conversion: patchStyleConversion(importers?.u),
        priority: 1,
      }),
    };
  }

  isSimpleText() {
    return false;
  }
}

const COLOR_SWATCHES = [
  [
    "#000000",
    "#434343",
    "#666666",
    "#999999",
    "#b7b7b7",
    "#cccccc",
    "#d9d9d9",
    "#efefef",
    "#f3f3f3",
    "#ffffff",
  ],
  [
    "#ff0000",
    "#ff9900",
    "#ffff00",
    "#00ff00",
    "#00ffff",
    "#4a86e8",
    "#0000ff",
    "#9900ff",
    "#ff00ff",
    "#ff99cc",
  ],
  [
    "#f4cccc",
    "#fce5cd",
    "#fff2cc",
    "#d9ead3",
    "#d0e0e3",
    "#cfe2f3",
    "#d9d2e9",
    "#ead1dc",
    "#f3d6f6",
    "#fff2fa",
  ],
  [
    "#ea9999",
    "#f9cb9c",
    "#ffe599",
    "#b6d7a8",
    "#a2c4c9",
    "#9fc5e8",
    "#b4a7d6",
    "#d5a6bd",
    "#c27ba0",
    "#dd7e6b",
  ],
  [
    "#cc0000",
    "#e69138",
    "#f1c232",
    "#6aa84f",
    "#45818e",
    "#3d85c6",
    "#674ea7",
    "#a64d79",
    "#85200c",
    "#783f04",
  ],
  [
    "#990000",
    "#b45f06",
    "#bf9000",
    "#38761d",
    "#134f5c",
    "#0b5394",
    "#351c75",
    "#741b47",
    "#5b0f00",
    "#4c1130",
  ],
];

const EMOJI_SET = [
  "😀",
  "😁",
  "😂",
  "🤣",
  "😊",
  "😍",
  "😘",
  "😎",
  "😇",
  "🙂",
  "🤔",
  "🤩",
  "🤗",
  "😴",
  "🤯",
  "😢",
  "😭",
  "😤",
  "😡",
  "👍",
  "👎",
  "🙏",
  "👏",
  "🎉",
  "🔥",
];

const ENABLE_ATTACHMENT_SELECTION = true;

const FORWARD_IFRAME_BASE_STYLES = `
  :root {
    color-scheme: light;
  }
  body {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    font-size: 14px;
    line-height: 1.5;
    margin: 0;
    padding: 12px;
    color: #0f172a;
    background: #ffffff;
    overflow-wrap: break-word;
  }
  * {
    box-sizing: border-box;
    max-width: 100%;
  }
  a {
    color: inherit;
  }
  table {
    border-collapse: collapse;
    table-layout: fixed;
    width: 100%;
  }
`;

/**
 * Normalizes email subject by ensuring only one Re: or Fw:/Fwd: prefix (case-insensitive)
 * @param {string} subject - The subject to normalize
 * @param {string} prefix - The prefix to add ("Re" or "Fwd")
 * @returns {string} - Normalized subject with single prefix
 */
const normalizeSubjectPrefix = (subject, prefix) => {
  if (!subject || typeof subject !== "string") {
    return prefix === "Re" ? "Re: " : "Fwd: ";
  }

  const trimmed = subject.trim();
  if (!trimmed) {
    return prefix === "Re" ? "Re: " : "Fwd: ";
  }

  // Remove all existing Re: and Fw:/Fwd: prefixes (case-insensitive)
  let cleaned = trimmed;
  const rePattern = /^(re:\s*)+/i;
  const fwdPattern = /^(fw:|fwd:)\s*/i;

  // Keep removing prefixes until none are found
  let previousCleaned = "";
  while (cleaned !== previousCleaned) {
    previousCleaned = cleaned;
    cleaned = cleaned.replace(rePattern, "").replace(fwdPattern, "").trim();
  }

  // Add the single prefix
  if (prefix === "Re") {
    return `Re: ${cleaned}`;
  } else {
    return `Fwd: ${cleaned}`;
  }
};

const looksLikeBase64 = (value) => {
  if (!value || typeof value !== "string") return false;

  const sanitized = value
    .replace(/\s+/g, "")
    .replace(/-/g, "+")
    .replace(/_/g, "/");

  if (!sanitized || /[^A-Za-z0-9+/=]/.test(sanitized)) return false;

  if (sanitized.length % 4 === 1) return false;

  const paddingLength = (4 - (sanitized.length % 4 || 4)) % 4;
  const padded = sanitized + "=".repeat(paddingLength);

  try {
    const decoded = atob(padded);
    const reencoded = btoa(decoded).replace(/=+$/, "");
    const normalizedOriginal = sanitized.replace(/=+$/, "");
    return reencoded === normalizedOriginal;
  } catch (error) {
    return false;
  }
};

const isMostlyPrintable = (value) => {
  if (!value || typeof value !== "string") return true;
  let printable = 0;
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (
      (code >= 32 && code <= 126) ||
      code === 9 ||
      code === 10 ||
      code === 13
    ) {
      printable += 1;
    }
  }
  return printable / value.length >= 0.85;
};

const decodeBodyData = (data) => {
  if (!data || typeof data !== "string") return "";

  if (!looksLikeBase64(data)) return data;

  try {
    const sanitized = data
      .replace(/\s+/g, "")
      .replace(/-/g, "+")
      .replace(/_/g, "/");

    const paddingLength = (4 - (sanitized.length % 4 || 4)) % 4;
    const padded = sanitized + "=".repeat(paddingLength);
    const decoded = atob(padded);

    let unicodeDecoded = decoded;

    try {
      unicodeDecoded = decodeURIComponent(
        Array.prototype.map
          .call(
            decoded,
            (c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2),
          )
          .join(""),
      );
    } catch (uriError) {
      unicodeDecoded = decoded;
    }

    if (!isMostlyPrintable(unicodeDecoded)) {
      return data;
    }

    return unicodeDecoded;
  } catch (decodeError) {
    return data;
  }
};

const sanitizeEmailBody = (html) => {
  if (!html || typeof html !== "string") return "";
  if (typeof window === "undefined" || typeof DOMParser === "undefined") {
    return html
      .replace(/<br\s*\/?>(?=\n?)/gi, "\n")
      .replace(/<\/p>/gi, "\n\n")
      .replace(/<\/div>/gi, "\n")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .trim();
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    doc
      .querySelectorAll("script,style,link[rel='stylesheet'],iframe")
      .forEach((el) => el.remove());
    return doc.body.innerHTML || "";
  } catch (error) {
    return html;
  }
};

const escapeHtml = (text) => {
  if (text === null || text === undefined) return "";
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

const plainTextToHtml = (text) => {
  if (!text) return "";
  return `<div style="white-space: pre-wrap;">${escapeHtml(text).replace(
    /\n/g,
    "<br>",
  )}</div>`;
};

const VALID_URL_SCHEMES = new Set(["http:", "https:", "mailto:", "tel:"]);

const normalizeUrl = (value) => {
  if (!value) return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/^[a-zA-Z][\w+.-]*:/.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
};

const validateLinkUrl = (value) => {
  if (!value) return false;
  try {
    const parsed = new URL(
      value,
      typeof window !== "undefined"
        ? window.location.origin
        : "http://localhost",
    );
    return VALID_URL_SCHEMES.has(parsed.protocol);
  } catch (error) {
    return false;
  }
};

const getStyleValue = (styleString, property) => {
  if (!styleString) return "";
  const regex = new RegExp(`${property}\\s*:\\s*([^;]+)`, "i");
  const match = styleString.match(regex);
  return match ? match[1].trim() : "";
};

const sanitizeHexColor = (value, fallback) => {
  if (!value) return fallback;
  const trimmed = value.trim();
  if (trimmed === "transparent") return fallback;
  return trimmed.startsWith("#") ? trimmed : fallback;
};

const sanitizeBgColor = (value) => {
  if (!value || value.trim() === "transparent") {
    return DEFAULT_BG_COLOR;
  }
  const trimmed = value.trim();
  return trimmed.startsWith("#") ? trimmed : DEFAULT_BG_COLOR;
};

const getSelectedNode = (selection) => {
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();
  if (anchorNode === focusNode) {
    return anchorNode;
  }
  return selection.isBackward() ? focusNode : anchorNode;
};

const PrefillContentPlugin = ({ contentHTML, loadKey }) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (loadKey === null || loadKey === undefined) {
      return;
    }
    if (typeof window === "undefined" || typeof DOMParser === "undefined") {
      return;
    }
    editor.update(() => {
      const root = $getRoot();
      root.clear();
      if (contentHTML) {
        const parser = new DOMParser();
        const dom = parser.parseFromString(contentHTML, "text/html");
        const nodes = $generateNodesFromDOM(editor, dom);
        nodes.forEach((node) => {
          if ($isElementNode(node)) {
            root.append(node);
          } else if ($isTextNode(node)) {
            const paragraph = $createParagraphNode();
            paragraph.append(node);
            root.append(paragraph);
          }
        });
      }
      root.selectStart();
    });
  }, [contentHTML, editor, loadKey]);

  return null;
};

function getEditorText(editor) {
  let text = "";

  editor.getEditorState().read(() => {
    const root = $getRoot();
    text = root.getTextContent();
  });

  return text;
}

function AIToolbarButton({ signature: receivedSignature }) {
  const [editor] = useLexicalComposerContext();

  const [loading, setLoading] = useState(false);

  // convert HTML to plain text
  const convertHtmlToPlainText = (html) => {
    if (!html) return "";
    const temp = document.createElement("div");
    temp.innerHTML = html;
    return (temp.textContent || temp.innerText || "").trim();
  };

  const runAI = async () => {
    // 1. Snapshot editor content
    // const editorHtml = getEditorHTML(editor);

    // const { content, signature } = stripSignature(
    //   editorHtml,
    //   receivedSignature,
    // );
    // console.log(content);

    // get signature as text
    const plainTextSignature = convertHtmlToPlainText(receivedSignature);

    // get editor context as text
    const content = editor.getEditorState().read(() => {
      const root = $getRoot();
      return root.getTextContent();
    });

    // remove signature text from content
    const promptBody = content.replace(plainTextSignature, "").trim();

    // 2. Call AI (outside editor)

    const payload = {
      model: "gemma3:270m",
      prompt:
        "body: " +
        promptBody +
        "\n\nRewrite the above email in a more professional tone. Respond with only the rewritten email content, do not include any additional text.",
      stream: false,
    };
    setLoading(true);
    const { data } = await axios.post(
      "http://olama.sportswearerp.com:11434/api/generate",
      payload,
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    const aiResponse = data?.response || "";
    setLoading(false);

    // 3. Insert AI response + signature
    editor.update(() => {
      const root = $getRoot();
      root.clear();

      root.append($createParagraphNode().append($createTextNode(aiResponse)));

      const parser = new DOMParser();

      const dom = parser.parseFromString(receivedSignature, "text/html");
      const nodes = $generateNodesFromDOM(editor, dom);

      nodes.forEach((node) => {
        if ($isElementNode(node)) {
          root.append(node);
        } else if ($isTextNode(node)) {
          const paragraph = $createParagraphNode();
          paragraph.append(node);
          root.append(paragraph);
        }
      });
      root.selectStart();
    });
  };

  return (
    <>
      {loading && <Spinner />}
      <button className="btn btn-sm py-0 px-1 btn-success" onClick={runAI}>
        <i className="mdi mdi-robot"></i> Ai Rewrite
      </button>
    </>
  );
}

// const AiReWritePlugin = ({signature,runFunc}) => {
// const [editor] = useLexicalComposerContext();

//     // 2. Call AI (outside editor)
//     const aiResponse = await fetch("/api/ai", {
//       method: "POST",
//       body: JSON.stringify({ content }),
//     }).then(res => res.text());

// useEffect(()=> {

// },[runFunc])

// return null

// }

export default function GmailComposeModal({
  show,
  setShow,
  replyTo = false,
  message = null,
  replyToMessageId = null,
  refreshFunc = null,
  closeFunc = null,
  forwardAttachments = null,
  forwardEmail = false,
}) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.userInfo);
  const inboxContext = useInbox();
  const {
    labels = [],
    refreshLabels = () => {},
    selectedLabel: inboxSelectedLabel = "",
  } = inboxContext || {};

  const fileInputRef = useRef(null);
  const forwardIframeRef = useRef(null);

  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [editorHtml, setEditorHtml] = useState("");
  const [selectedLabel, setSelectedLabel] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [selectedForwardAttachments, setSelectedForwardAttachments] = useState(
    () => (forwardAttachments || []).map((att) => att.id),
  );
  const [downloadingAttachments, setDownloadingAttachments] = useState(false);
  const [sending, setSending] = useState(false);
  const [composerKey, setComposerKey] = useState(0);
  const [initialHtmlContent, setInitialHtmlContent] = useState("");

  useEffect(() => {
    if (inboxSelectedLabel) {
      setSelectedLabel(inboxSelectedLabel);
    }
  }, [inboxSelectedLabel]);

  const parsedEmailContent = useMemo(() => {
    if (!forwardEmail || !message) {
      return { content: "", isHtml: false, originalContent: "" };
    }

    const rawBodyData = message?.bodyData ?? "";
    const rawBodyFallback = message?.body ?? "";
    const sourceBody = rawBodyData || rawBodyFallback;
    const decodedSource = decodeBodyData(sourceBody);
    const effectiveBody =
      rawBodyData && decodedSource === sourceBody
        ? rawBodyFallback
        : decodedSource;

    const content = effectiveBody ?? "";
    const htmlPattern = /<\/?[a-z][\s\S]*>/i;

    if (!content) {
      return { content: "", isHtml: false, originalContent: "" };
    }

    if (htmlPattern.test(content)) {
      return {
        content: sanitizeEmailBody(content),
        isHtml: true,
        originalContent: content,
      };
    }

    return { content, isHtml: false, originalContent: content };
  }, [forwardEmail, message]);

  const formatForwardHeader = useCallback(() => {
    if (!message || !forwardEmail) return "";

    const fromEmail =
      message.forwardFrom?.[0]?.email || message.from?.[0]?.email || "";
    const fromName = message.fromName || "";
    const fromDisplay =
      fromName && fromEmail
        ? `${fromName} <${fromEmail}>`
        : fromEmail
          ? `<${fromEmail}>`
          : fromName;

    const date = message.date ? new Date(message.date) : new Date();
    const dateStr = date.toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
    const timeStr = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    const toEmail = message.to?.[0]?.email || "";
    const toDisplay = toEmail ? `<${toEmail}>` : "";
    const subjectText = message.subject || "(no subject)";

    const headerLines = [
      "---------- Forwarded message ---------",
      `From: ${fromDisplay}`,
      `Date: ${dateStr} at ${timeStr}`,
      `Subject: ${subjectText}`,
      `To: ${toDisplay}`,
    ];

    if (message.cc) {
      headerLines.push(`Cc: ${message.cc}`);
    }

    return headerLines.join("\n");
  }, [forwardEmail, message]);

  const forwardTemplateHtml = useMemo(() => {
    if (!forwardEmail || !message) {
      return "";
    }

    const header = formatForwardHeader();
    const headerHtml = header
      ? `<div style="color: #888; font-family: monospace; white-space: pre-wrap;">${escapeHtml(
          header,
        ).replace(/\n/g, "<br>")}</div><br>`
      : "";

    const sourceHtml = parsedEmailContent.isHtml
      ? parsedEmailContent.originalContent || parsedEmailContent.content
      : plainTextToHtml(parsedEmailContent.content);

    return `${headerHtml}${sourceHtml}`;
  }, [forwardEmail, formatForwardHeader, message, parsedEmailContent]);

  const lexicalTheme = useMemo(
    () => ({
      paragraph: "compose-editor-paragraph",
      text: {
        underline: "compose-editor-underline",
        underlineStrikethrough: "compose-editor-underline-strike",
      },
      link: "compose-editor-link",
    }),
    [],
  );

  const editorConfig = useMemo(
    () => ({
      namespace: "gmail-compose-editor",
      theme: lexicalTheme,
      nodes: [
        HeadingNode,
        QuoteNode,
        ListNode,
        ListItemNode,
        LinkNode,
        ExtendedTextNode,
        {
          replace: TextNode,
          with: (node) => new ExtendedTextNode(node.__text),
          withKlass: ExtendedTextNode,
        },
      ],
      onError(error) {
        console.error("Lexical editor error", error);
      },
    }),
    [lexicalTheme],
  );

  const resetEditorState = useCallback((prefillHTML) => {
    setComposerKey((key) => key + 1);
    setInitialHtmlContent(prefillHTML || "");
    setBody("");
    setEditorHtml(prefillHTML || "");
  }, []);

  const processSignatureHtml = useCallback((signature) => {
    if (!signature) return "";

    // Remove leading and trailing empty paragraphs
    let cleaned = signature.trim();
    cleaned = cleaned.replace(
      /^(<p(?:\s[^>]*)?>(?:<br\s*\/?>(?:\s|&nbsp;)*|\s|&nbsp;)*<\/p>)+/gi,
      "",
    );
    cleaned = cleaned.replace(
      /(<p(?:\s[^>]*)?>(?:<br\s*\/?>(?:\s|&nbsp;)*|\s|&nbsp;)*<\/p>)+$/gi,
      "",
    );

    // Wrap in a div with inline styles to remove paragraph margins
    cleaned = `<div style="margin: 0; padding: 0;">${cleaned}</div>`;

    // Add inline styles to all p tags to remove margins (including <p class="...">)
    cleaned = cleaned.replace(/<p(\s[^>]*)?>/gi, (match, attrs = "") => {
      const extraStyle = "margin: 0; padding: 0; line-height: 1.4;";
      const styleMatch = attrs.match(/\sstyle=(['"])(.*?)\1/i);

      if (styleMatch) {
        const currentStyle = styleMatch[2].trim();
        const mergedStyle = `${currentStyle}${
          currentStyle.endsWith(";") ? " " : "; "
        }${extraStyle}`;
        return `<p${attrs.replace(
          styleMatch[0],
          ` style=${styleMatch[1]}${mergedStyle}${styleMatch[1]}`,
        )}>`;
      }

      return `<p${attrs} style="${extraStyle}">`;
    });

    // Add spacing before signature
    cleaned = `<p><br></p><p><br></p>${cleaned}`;

    return cleaned.trim();
  }, []);

  const processedSignature = useMemo(
    () => processSignatureHtml(user?.emailSignature),
    [processSignatureHtml, user],
  );

  const initializeCompose = useCallback(() => {
    let initialTo = forwardEmail ? "" : message?.from?.[0]?.email || "";
    if (replyTo && message) {
      initialTo = message?.replyTo?.[0]?.email || initialTo;
    }

    let initialSubject = message?.subject || "";

    if (replyToMessageId) {
      initialSubject = normalizeSubjectPrefix(initialSubject, "Re");
    }

    if (forwardEmail) {
      initialSubject = normalizeSubjectPrefix(initialSubject, "Fwd");
    }

    setTo(initialTo);
    setSubject(initialSubject);
    setSelectedLabel(inboxSelectedLabel || "");
    setAttachments([]);
    setSelectedForwardAttachments(
      (forwardAttachments || []).map((att) => att.id),
    );
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }

    // Prepare initial content with the user's signature (forwarded block handled separately)
    const initialContent = processedSignature || "";

    resetEditorState(initialContent);
  }, [
    forwardAttachments,
    forwardEmail,
    inboxSelectedLabel,
    message,
    replyToMessageId,
    resetEditorState,
    processedSignature,
    user,
  ]);

  useEffect(() => {
    if (show) {
      initializeCompose();
    }
  }, [initializeCompose, show]);

  const writeForwardIframe = useCallback((htmlContent = "") => {
    const iframe = forwardIframeRef.current;
    if (!iframe) {
      return;
    }
    const documentRef =
      iframe.contentDocument || iframe.contentWindow?.document;
    if (!documentRef) {
      return;
    }
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8" />
        <style>${FORWARD_IFRAME_BASE_STYLES}</style></head>
        <body contenteditable="true">${htmlContent}</body></html>`;
    documentRef.open();
    documentRef.write(html);
    documentRef.close();
  }, []);

  const loadForwardedContent = useCallback(() => {
    if (!forwardEmail || !show) {
      writeForwardIframe("");
      return;
    }
    writeForwardIframe(forwardTemplateHtml || "");
  }, [forwardEmail, forwardTemplateHtml, show, writeForwardIframe]);

  useEffect(() => {
    loadForwardedContent();
  }, [loadForwardedContent]);

  const handleEditorChange = (editorState, editor) => {
    editorState.read(() => {
      const plainText = $getRoot().getTextContent();
      setBody(plainText);
      const htmlString = $generateHtmlFromNodes(editor, null);
      setEditorHtml(htmlString);
    });
  };

  const Placeholder = () => (
    <div className="compose-editor-placeholder">Write your email here...</div>
  );

  const formatBytes = (bytes) => {
    if (!bytes || Number.isNaN(Number(bytes))) return "";
    const sizes = ["B", "KB", "MB", "GB"];
    const value = Number(bytes);
    if (value === 0) return "0 B";
    const index = Math.min(
      Math.floor(Math.log(value) / Math.log(1024)),
      sizes.length - 1,
    );
    const converted = value / Math.pow(1024, index);
    return `${converted.toFixed(converted >= 10 || index === 0 ? 0 : 1)} ${
      sizes[index]
    }`;
  };

  const downloadAttachmentAsFile = async (attachment, messageId) => {
    try {
      const response = await API.get(
        `/emails/attachment/${messageId}/${attachment.id}`,
        {
          responseType: "blob",
          headers: authHeader(),
        },
      );

      const blob = response.data;
      const filename = attachment.filename || "attachment";
      const contentType =
        attachment.contentType ||
        attachment.content_type ||
        blob.type ||
        "application/octet-stream";

      return new File([blob], filename, { type: contentType });
    } catch (error) {
      console.error("Error downloading attachment:", error);
      throw error;
    }
  };

  const handleFileChange = (event) => {
    setAttachments(Array.from(event.target.files || []));
  };

  const handleForwardAttachmentToggle = (id, checked) => {
    setSelectedForwardAttachments((prev) => {
      if (checked) {
        return prev.includes(id) ? prev : [...prev, id];
      }
      return prev.filter((item) => item !== id);
    });
  };

  const closeCompose = useCallback(() => {
    if (closeFunc) {
      closeFunc();
    }
    setShow(false);
    setTo("");
    setSubject("");
    setBody("");
    setEditorHtml("");
    setAttachments([]);
    setSelectedForwardAttachments([]);
    setInitialHtmlContent("");
    setSelectedLabel(inboxSelectedLabel || "");
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
    writeForwardIframe("");
  }, [closeFunc, inboxSelectedLabel, setShow, writeForwardIframe]);

  const handleSend = async () => {
    const trimmedTo = to.trim();
    const trimmedSubject = subject.trim();
    const trimmedBody = body.trim();
    let finalBody = editorHtml?.trim() || "";
    const forwardDocument = forwardIframeRef.current
      ? forwardIframeRef.current.contentDocument ||
        forwardIframeRef.current.contentWindow?.document
      : null;
    const forwardSectionHtml = forwardEmail
      ? forwardDocument?.body?.innerHTML || forwardTemplateHtml || ""
      : "";

    if (!trimmedTo) {
      dispatch(
        setNotification({ type: "error", message: "Please add a recipient" }),
      );
      return;
    }
    if (!trimmedSubject) {
      dispatch(
        setNotification({ type: "error", message: "Please add a subject" }),
      );
      return;
    }
    if (!trimmedBody && !forwardEmail) {
      dispatch(
        setNotification({ type: "error", message: "Please add a message" }),
      );
      return;
    }
    if (!finalBody && trimmedBody) {
      finalBody = plainTextToHtml(trimmedBody);
    }

    if (forwardEmail) {
      const normalizedForwardHtml = forwardSectionHtml.trim();
      if (normalizedForwardHtml) {
        finalBody = [finalBody, normalizedForwardHtml]
          .filter(Boolean)
          .join("<br><br>");
      }
    }

    if (!finalBody) {
      dispatch(
        setNotification({
          type: "error",
          message: "Unable to prepare message",
        }),
      );
      return;
    }

    setSending(true);
    setDownloadingAttachments(true);

    try {
      const formData = new FormData();
      formData.append("to", trimmedTo);
      formData.append("subject", trimmedSubject);
      formData.append("text", finalBody);
      formData.append("isHtml", "true");

      if (replyToMessageId) {
        formData.append("replyToMessageId", replyToMessageId);
      }

      if (
        selectedLabel &&
        selectedLabel !== "INBOX" &&
        selectedLabel !== "TRASH" &&
        selectedLabel !== "SPAM" &&
        selectedLabel !== "SENT"
      ) {
        formData.append("labelId", selectedLabel);
      }
      if (forwardEmail) {
        formData.append("isForward", "true");
      }

      attachments.forEach((file) => {
        formData.append("attachments", file);
      });

      const normalizedForwardAttachments = forwardAttachments || [];
      if (normalizedForwardAttachments.length > 0) {
        const attachmentsToForward = ENABLE_ATTACHMENT_SELECTION
          ? normalizedForwardAttachments.filter((att) =>
              selectedForwardAttachments.includes(att.id),
            )
          : normalizedForwardAttachments;

        for (const att of attachmentsToForward) {
          try {
            const file = await downloadAttachmentAsFile(att, message?.id);
            formData.append("attachments", file);
          } catch (error) {
            dispatch(
              setNotification({
                type: "warning",
                message: `Failed to attach ${att.filename || "attachment"}`,
              }),
            );
          }
        }
      }

      setDownloadingAttachments(false);

      const { data } = await API.post("/emails/send", formData, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
      });

      if (data?.success) {
        dispatch(
          setNotification({ type: "success", message: data.message || "Sent" }),
        );
        // refreshLabels();
        if (message && refreshFunc) {
          await refreshFunc(message.id);
        }
        closeCompose();
      } else {
        dispatch(
          setNotification({
            type: "error",
            message: data?.message || "Failed",
          }),
        );
      }
    } catch (error) {
      dispatch(setNotification({ type: "error", message: error.message }));
    } finally {
      setSending(false);
      setDownloadingAttachments(false);
    }
  };

  const normalizedForwardAttachments = forwardAttachments || [];

  const ToolbarButton = ({
    label,
    active,
    onClick,
    title,
    disabled,
    children,
  }) => (
    <button
      type="button"
      className={`compose-toolbar-btn ${active ? "active" : ""}`}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      title={title}
      disabled={disabled}
    >
      {children || label}
    </button>
  );

  const ToolbarSelect = ({ value, options, onChange, title, width }) => (
    <select
      className="compose-toolbar-select"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      title={title}
      style={{ minWidth: width || 110 }}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );

  const ColorPaletteControl = ({
    label,
    title,
    value,
    onSelect,
    allowTransparent = false,
    mode = "text",
  }) => {
    const [open, setOpen] = useState(false);
    const controlRef = useRef(null);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (controlRef.current && !controlRef.current.contains(event.target)) {
          setOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSwatchSelect = (color) => {
      onSelect(color);
      setOpen(false);
    };

    const indicatorStyle =
      mode === "text"
        ? { borderBottom: `2px solid ${value}` }
        : {
            backgroundColor: value === DEFAULT_BG_COLOR ? "#ffffff" : value,
            border: `2px solid ${value === DEFAULT_BG_COLOR ? "#e2e8f0" : value}`,
            backgroundImage:
              value === DEFAULT_BG_COLOR
                ? "linear-gradient(135deg, #e2e8f0 25%, transparent 25%, transparent 50%, #e2e8f0 50%, #e2e8f0 75%, transparent 75%, transparent)"
                : "none",
            backgroundSize: value === DEFAULT_BG_COLOR ? "6px 6px" : "auto",
          };

    const isSelected = (color) => {
      const normalizedColor = (
        allowTransparent && color === "transparent" ? DEFAULT_BG_COLOR : color
      ).toLowerCase();
      const current = (
        value || (mode === "highlight" ? DEFAULT_BG_COLOR : DEFAULT_TEXT_COLOR)
      ).toLowerCase();
      return normalizedColor === current;
    };

    return (
      <div className="compose-color-palette" ref={controlRef}>
        <button
          type="button"
          className="compose-color-button"
          title={title}
          onClick={() => setOpen((prev) => !prev)}
        >
          <span className="compose-color-label">{label}</span>
          <span className="compose-color-indicator" style={indicatorStyle} />
          <span className="compose-color-caret">▾</span>
        </button>
        {open && (
          <div className="compose-color-panel">
            {allowTransparent && (
              <button
                type="button"
                className={`compose-color-swatch transparent ${
                  isSelected("transparent") ? "selected" : ""
                }`}
                onClick={() => handleSwatchSelect("transparent")}
                aria-label="No highlight"
              >
                {isSelected("transparent") && <span>✓</span>}
              </button>
            )}
            {COLOR_SWATCHES.map((row, rowIndex) => (
              <div className="compose-color-row" key={`row-${rowIndex}`}>
                {row.map((color) => (
                  <button
                    type="button"
                    key={color}
                    className={`compose-color-swatch ${
                      isSelected(color) ? "selected" : ""
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => handleSwatchSelect(color)}
                    aria-label={`${title} ${color}`}
                  >
                    {isSelected(color) && <span>✓</span>}
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const AlignIcon = ({ variant }) => {
    const commonProps = {
      width: 18,
      height: 18,
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: 1.6,
      strokeLinecap: "round",
      strokeLinejoin: "round",
      "aria-hidden": true,
      focusable: false,
    };

    const lines = {
      left: [
        { x1: 4, y1: 7, x2: 16, y2: 7 },
        { x1: 4, y1: 12, x2: 20, y2: 12 },
        { x1: 4, y1: 17, x2: 14, y2: 17 },
      ],
      center: [
        { x1: 6, y1: 7, x2: 18, y2: 7 },
        { x1: 4, y1: 12, x2: 20, y2: 12 },
        { x1: 6, y1: 17, x2: 18, y2: 17 },
      ],
      right: [
        { x1: 8, y1: 7, x2: 20, y2: 7 },
        { x1: 4, y1: 12, x2: 20, y2: 12 },
        { x1: 10, y1: 17, x2: 20, y2: 17 },
      ],
      justify: [
        { x1: 4, y1: 7, x2: 20, y2: 7 },
        { x1: 4, y1: 12, x2: 20, y2: 12 },
        { x1: 4, y1: 17, x2: 20, y2: 17 },
      ],
    };

    return (
      <svg {...commonProps}>
        {lines[variant].map((line, index) => (
          <line key={`${variant}-${index}`} {...line} />
        ))}
      </svg>
    );
  };

  const ListIcon = ({ type }) => {
    const commonProps = {
      width: 18,
      height: 18,
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: 1.6,
      strokeLinecap: "round",
      strokeLinejoin: "round",
      "aria-hidden": true,
      focusable: false,
    };

    if (type === "number") {
      return (
        <svg {...commonProps}>
          <path d="M5 6h2v6" />
          <path d="M5 9h2" />
          <line x1={9} y1={6} x2={20} y2={6} />
          <line x1={9} y1={12} x2={20} y2={12} />
          <line x1={9} y1={18} x2={20} y2={18} />
        </svg>
      );
    }

    return (
      <svg {...commonProps}>
        <circle cx={6} cy={6} r={1.3} />
        <circle cx={6} cy={12} r={1.3} />
        <circle cx={6} cy={18} r={1.3} />
        <line x1={9} y1={6} x2={20} y2={6} />
        <line x1={9} y1={12} x2={20} y2={12} />
        <line x1={9} y1={18} x2={20} y2={18} />
      </svg>
    );
  };

  const IndentIcon = ({ direction }) => (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable={false}
    >
      <line x1={4} y1={6} x2={20} y2={6} />
      <line x1={4} y1={12} x2={20} y2={12} />
      <line x1={4} y1={18} x2={20} y2={18} />
      {direction === "in" ? (
        <polyline points="9 9 12 12 9 15" />
      ) : (
        <polyline points="15 9 12 12 15 15" />
      )}
    </svg>
  );

  const LinkIcon = () => (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable={false}
    >
      <path d="M10.5 13.5l-1.5 1.5a3 3 0 104.24 4.24l3-3a3 3 0 00-4.24-4.24L11 12" />
      <path d="M13.5 10.5l1.5-1.5a3 3 0 10-4.24-4.24l-3 3A3 3 0 008 12.5L9 13" />
    </svg>
  );

  const EmojiIcon = () => (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable={false}
    >
      <circle cx={12} cy={12} r={8} />
      <path d="M9 10h.01" />
      <path d="M15 10h.01" />
      <path d="M8.5 14A4.5 4.5 0 0012 15.5 4.5 4.5 0 0015.5 14" />
    </svg>
  );

  const EmojiPickerControl = ({ onSelect }) => {
    const [open, setOpen] = useState(false);
    const controlRef = useRef(null);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (controlRef.current && !controlRef.current.contains(event.target)) {
          setOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSelect = (emoji) => {
      onSelect(emoji);
      setOpen(false);
    };

    return (
      <div className="compose-emoji-picker" ref={controlRef}>
        <button
          type="button"
          className="compose-toolbar-btn"
          title="Insert emoji"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => setOpen((prev) => !prev)}
        >
          <EmojiIcon />
        </button>
        {open && (
          <div className="compose-emoji-panel">
            {EMOJI_SET.map((emoji) => (
              <button
                type="button"
                key={emoji}
                className="compose-emoji-button"
                onClick={() => handleSelect(emoji)}
                aria-label={`Insert ${emoji}`}
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const ToolbarPlugin = () => {
    const [editor] = useLexicalComposerContext();
    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isUnderline, setIsUnderline] = useState(false);
    const [isLink, setIsLink] = useState(false);
    const [blockType, setBlockType] = useState("paragraph");
    const [elementFormat, setElementFormat] = useState("left");
    const [fontFamily, setFontFamily] = useState(FONT_FAMILIES[0].value);
    const [fontSize, setFontSize] = useState(FONT_SIZE_OPTIONS[1].value);
    const [textColor, setTextColor] = useState(DEFAULT_TEXT_COLOR);
    const [bgColor, setBgColor] = useState(DEFAULT_BG_COLOR);

    const applyStyleToSelection = useCallback(
      (styles) => {
        editor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            $patchStyleText(selection, styles);
          }
        });
      },
      [editor],
    );

    const handleFontFamilyChange = (value) => {
      setFontFamily(value);
      applyStyleToSelection({ "font-family": value });
    };

    const handleFontSizeChange = (value) => {
      setFontSize(value);
      applyStyleToSelection({ "font-size": value });
    };

    const handleTextColorChange = (value) => {
      setTextColor(value);
      applyStyleToSelection({ color: value });
    };

    const handleBgColorChange = (value) => {
      const normalized = value === DEFAULT_BG_COLOR ? DEFAULT_BG_COLOR : value;
      setBgColor(normalized);
      applyStyleToSelection({
        "background-color":
          normalized === DEFAULT_BG_COLOR ? "transparent" : normalized,
      });
    };

    const appendSignatureToEnd = () => {
      editor.update(() => {
        const root = $getRoot();

        const parser = new DOMParser();
        // const signature = processSignatureHtml(processedSignature);
        console.log(processedSignature);

        const dom = parser.parseFromString(processedSignature, "text/html");
        const nodes = $generateNodesFromDOM(editor, dom);
        console.log(nodes);

        nodes.forEach((node) => {
          if ($isElementNode(node)) {
            root.append(node);
          } else if ($isTextNode(node)) {
            const paragraph = $createParagraphNode();
            paragraph.append(node);
            root.append(paragraph);
          }
        });

        // Finally append to root
        // root.append(nodes);
        root.selectStart();
      });
    };

    const updateToolbar = useCallback(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        setIsBold(selection.hasFormat("bold"));
        setIsItalic(selection.hasFormat("italic"));
        setIsUnderline(selection.hasFormat("underline"));

        const anchorNode = selection.anchor.getNode();
        const topLevel =
          anchorNode.getKey() === "root"
            ? anchorNode
            : anchorNode.getTopLevelElementOrThrow();
        const elementType = topLevel.getType();
        const normalizedType =
          elementType === "root" ? "paragraph" : elementType;
        const formatType =
          typeof topLevel.getFormatType === "function"
            ? topLevel.getFormatType()
            : "left";
        if (
          elementType === "list" &&
          typeof topLevel.getListType === "function"
        ) {
          setBlockType(topLevel.getListType());
        } else {
          setBlockType(normalizedType);
        }
        setElementFormat(formatType || "left");

        const node = getSelectedNode(selection);
        const parent = node ? node.getParent() : null;
        if ($isLinkNode(node) || $isLinkNode(parent)) {
          setIsLink(true);
        } else {
          setIsLink(false);
        }
        if ($isTextNode(node)) {
          const style = node.getStyle();
          const resolvedFamily =
            getStyleValue(style, "font-family") || FONT_FAMILIES[0].value;
          const resolvedSize =
            getStyleValue(style, "font-size") || FONT_SIZE_OPTIONS[1].value;
          const resolvedColor = sanitizeHexColor(
            getStyleValue(style, "color"),
            DEFAULT_TEXT_COLOR,
          );
          const resolvedBg = sanitizeBgColor(
            getStyleValue(style, "background-color"),
          );
          setFontFamily(resolvedFamily);
          setFontSize(resolvedSize);
          setTextColor(resolvedColor);
          setBgColor(resolvedBg);
        } else {
          setFontFamily(FONT_FAMILIES[0].value);
          setFontSize(FONT_SIZE_OPTIONS[1].value);
          setTextColor(DEFAULT_TEXT_COLOR);
          setBgColor(DEFAULT_BG_COLOR);
        }
      } else {
        setIsLink(false);
      }
    }, []);

    useEffect(() => {
      return mergeRegister(
        editor.registerUpdateListener(({ editorState }) => {
          editorState.read(() => {
            updateToolbar();
          });
        }),
        editor.registerCommand(
          SELECTION_CHANGE_COMMAND,
          () => {
            editor.getEditorState().read(() => {
              updateToolbar();
            });
            return false;
          },
          COMMAND_PRIORITY_LOW,
        ),
      );
    }, [editor, updateToolbar]);

    const toggleList = (command, type) => {
      if (blockType === type) {
        editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
      } else {
        editor.dispatchCommand(command, undefined);
      }
    };

    const handleLinkButton = () => {
      if (isLink) {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
        return;
      }
      const urlInput =
        typeof window !== "undefined"
          ? window.prompt("Enter link URL", "https://")
          : null;
      const normalized = normalizeUrl(urlInput || "");
      if (normalized && validateLinkUrl(normalized)) {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, normalized);
      } else if (urlInput) {
        if (typeof window !== "undefined") {
          window.alert("Please enter a valid URL (http, https, mailto, tel)");
        }
      }
    };

    const handleEmojiInsert = (emoji) => {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          selection.insertText(emoji);
        }
      });
    };

    return (
      <div className="compose-editor-toolbar">
        <ToolbarButton
          label="↺"
          title="Undo"
          onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}
        />
        <ToolbarButton
          label="↻"
          title="Redo"
          onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)}
        />
        <span className="compose-toolbar-divider" />
        <ToolbarSelect
          value={fontFamily}
          options={FONT_FAMILIES}
          onChange={handleFontFamilyChange}
          title="Font family"
          width={130}
        />
        <ToolbarSelect
          value={fontSize}
          options={FONT_SIZE_OPTIONS}
          onChange={handleFontSizeChange}
          title="Font size"
          width={95}
        />
        <span className="compose-toolbar-divider" />
        <ToolbarButton
          label="B"
          title="Bold"
          active={isBold}
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold")}
        />
        <ToolbarButton
          label="I"
          title="Italic"
          active={isItalic}
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic")}
        />
        <ToolbarButton
          label="U"
          title="Underline"
          active={isUnderline}
          onClick={() =>
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline")
          }
        />
        <ToolbarButton
          label="Link"
          title={isLink ? "Remove link" : "Add link"}
          active={isLink}
          onClick={handleLinkButton}
        >
          <LinkIcon />
        </ToolbarButton>
        <EmojiPickerControl onSelect={handleEmojiInsert} />
        <ColorPaletteControl
          label="A"
          title="Text color"
          value={textColor}
          onSelect={handleTextColorChange}
        />
        <ColorPaletteControl
          label="A"
          title="Background color"
          value={bgColor}
          onSelect={handleBgColorChange}
          allowTransparent
          mode="highlight"
        />
        <span className="compose-toolbar-divider" />
        <ToolbarButton
          label="•"
          title="Bulleted list"
          active={blockType === "bullet"}
          onClick={() => toggleList(INSERT_UNORDERED_LIST_COMMAND, "bullet")}
        >
          <ListIcon type="bullet" />
        </ToolbarButton>
        <ToolbarButton
          label="1."
          title="Numbered list"
          active={blockType === "number"}
          onClick={() => toggleList(INSERT_ORDERED_LIST_COMMAND, "number")}
        >
          <ListIcon type="number" />
        </ToolbarButton>
        <span className="compose-toolbar-divider" />
        <ToolbarButton
          title="Align left"
          active={elementFormat === "left"}
          onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "left")}
        >
          <AlignIcon variant="left" />
        </ToolbarButton>
        <ToolbarButton
          title="Align center"
          active={elementFormat === "center"}
          onClick={() =>
            editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "center")
          }
        >
          <AlignIcon variant="center" />
        </ToolbarButton>
        <ToolbarButton
          title="Align right"
          active={elementFormat === "right"}
          onClick={() =>
            editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "right")
          }
        >
          <AlignIcon variant="right" />
        </ToolbarButton>
        <ToolbarButton
          title="Decrease indent"
          onClick={() =>
            editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined)
          }
        >
          <IndentIcon direction="out" />
        </ToolbarButton>
        <ToolbarButton
          title="Increase indent"
          onClick={() =>
            editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined)
          }
        >
          <IndentIcon direction="in" />
        </ToolbarButton>
        <span className="compose-toolbar-divider" />
        {/* Append Signature */}
        <ToolbarButton
          label="✍️"
          title="Append Signature"
          onClick={() => appendSignatureToEnd()}
        />
        <span className="compose-toolbar-spacer" />
        <AIToolbarButton signature={processedSignature} />
      </div>
    );
  };

  /* ******************** All UseEffects ************************* */

  return (
    <div>
      <GmailComposeStyles
        replyToMessageId={replyToMessageId}
        forwardEmail={forwardEmail}
      />
      <Modal
        show={show}
        onHide={closeCompose}
        centered
        dialogClassName="compose-modal-width"
        contentClassName="shadow-lg custom-border-radius px-1"
        style={{
          zIndex: 100005,
        }}
      >
        <Modal.Header className="py-0" closeButton>
          <Modal.Title>Compose Email</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ minHeight: "50vh" }}>
          <div className="compose-line-field">
            <label className="compose-field-label">To</label>
            <input
              type="email"
              className="compose-line-inputt form-control form-control-sm"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              placeholder="To:"
            />
          </div>
          <div className="compose-line-field">
            <label className="compose-field-label">Subject</label>
            <input
              type="text"
              className="compose-line-inputt form-control form-control-sm"
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              placeholder="Subject"
            />
          </div>
          <div className="compose-field-group">
            <label className="compose-field-label">Label</label>
            <select
              className="compose-field-select"
              value={selectedLabel || ""}
              onChange={(event) => setSelectedLabel(event.target.value)}
            >
              <option value="">-- Select Label --</option>
              {labels.map((label) => (
                <option key={label.labelId} value={label.labelId}>
                  {label.name}
                </option>
              ))}
            </select>
          </div>
          <div className="compose-editor-shell">
            <LexicalComposer key={composerKey} initialConfig={editorConfig}>
              <div className="compose-editor-container">
                <RichTextPlugin
                  contentEditable={
                    <ContentEditable className="compose-editor-area" />
                  }
                  placeholder={<Placeholder />}
                  ErrorBoundary={LexicalErrorBoundary}
                />
                <PrefillContentPlugin
                  contentHTML={initialHtmlContent}
                  loadKey={composerKey}
                />
                <LinkPlugin validateUrl={validateLinkUrl} />
                <HistoryPlugin />
                <ListPlugin />
                <AutoFocusPlugin />
                {/* <AiReWritePlugin /> */}
                <OnChangePlugin onChange={handleEditorChange} />
              </div>
              {processedSignature && (
                <ToolbarPlugin signature={processedSignature} />
              )}
            </LexicalComposer>
          </div>
          <div className="compose-field-group mt-2">
            <label className="compose-field-label">Attachments</label>
            <input
              type="file"
              multiple
              ref={fileInputRef}
              onChange={handleFileChange}
              className="form-control form-control-sm"
            />
            {attachments.length > 0 && (
              <div className="compose-attachments-list">
                {attachments.map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    className="compose-attachment-item"
                  >
                    {file.name}
                  </div>
                ))}
              </div>
            )}
          </div>

          {forwardEmail && (
            <div className="compose-forward-preview mt-3">
              <div className="compose-forward-section">
                <div className="compose-forward-label">
                  <i className="mdi mdi-email-forward-outline me-1" /> Forwarded
                  Email (click to edit)
                </div>
                <iframe
                  title="Forwarded email content"
                  className="compose-forward-iframe"
                  sandbox="allow-same-origin"
                  ref={forwardIframeRef}
                />
                <div className="compose-forward-hint">
                  Changes here are included in your sent message along with the
                  text above.
                </div>
              </div>
            </div>
          )}
          {normalizedForwardAttachments.length > 0 && (
            <div className="compose-field-group">
              <label className="compose-field-label">
                Forwarded Attachments ({normalizedForwardAttachments.length})
              </label>
              <div className="compose-forward-attachments">
                {ENABLE_ATTACHMENT_SELECTION
                  ? normalizedForwardAttachments.map((att, index) => (
                      <label
                        key={att.id || index}
                        className="compose-forward-option"
                      >
                        <input
                          type="checkbox"
                          checked={selectedForwardAttachments.includes(att.id)}
                          onChange={(event) =>
                            handleForwardAttachmentToggle(
                              att.id,
                              event.target.checked,
                            )
                          }
                        />
                        <span>
                          {att.filename || "Attachment"}
                          {att.size && ` (${formatBytes(att.size)})`}
                        </span>
                      </label>
                    ))
                  : normalizedForwardAttachments.map((att, index) => (
                      <div
                        key={att.id || index}
                        className="compose-forward-chip"
                      >
                        <i className="mdi mdi-paperclip me-1" />
                        {att.filename || "Attachment"}
                        {att.size && ` (${formatBytes(att.size)})`}
                      </div>
                    ))}
              </div>
            </div>
          )}
        </Modal.Body>
        <ModalFooter className="py-2 mt-0 d-flex justify-content-between">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={closeCompose}
          >
            Close
          </button>
          <div className="d-flex align-items-center gap-1">
            {/* {console.log("body", body)}
            {console.log("editorHtml", editorHtml)} */}
            {/* <AiRewrite
              body={body}
              setBody={setBody}
              editorHtml={editorHtml}
              setEditorHtml={setEditorHtml}
              signatureHtml={processedSignature}
            /> */}
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={handleSend}
              disabled={sending || downloadingAttachments}
            >
              {downloadingAttachments
                ? "Preparing attachments..."
                : sending
                  ? "Sending..."
                  : "Send"}
            </button>
          </div>
        </ModalFooter>
      </Modal>
    </div>
  );
}
